import { useEffect, useRef } from 'react';
import { useUIStore } from '../store/uiStore';
import { undoLatest } from '../utils/undoGroup';
import { isEscapeLayerOpen } from '../utils/escapeStack';
import { shouldHandleShortcut } from './shortcutGuard';

/** Element id the arrow keys focus after moving the selection. The row's
 *  keyboard button carries it (DefensePlanFeatureRow). */
export function planRowButtonId(featureId: string): string {
  return `plan-row-btn-${featureId}`;
}

export interface PlanShortcutActions {
  /** Strategy ids in tile order; key "1" is the first. */
  strategies: readonly string[];
  /** Same as pressing that tile. */
  onPickStrategy: (strategy: string) => void;
  /** Feature ids in on-screen order (expanded bundle members included). */
  visibleFeatureIds: readonly string[];
  /** Same as clicking the row: select and fit the map. */
  onSelectFeature: (featureId: string) => void;
  /** Same as the row's trash button. */
  onDeleteFeature: (featureId: string) => void;
  /** Delete with 2 or more defenses selected (Cmd/Ctrl/Shift+click). Without
   *  it, Delete falls back to onDeleteFeature on the anchor alone. */
  onDeleteSelection?: (ids: string[]) => void;
  /** Cmd/Ctrl+A while the layer is live: select every visible row. */
  onSelectAllRows?: () => void;
  /** Opens Assign; absent when the viewer can't assign. */
  onAssignFeature?: (featureId: string) => void;
  /** Opens the row's "Copy as" menu; absent when the plan can't be edited. */
  onCopyFeature?: (featureId: string) => void;
  /** Opens the row's ⋯ menu (Shift+F10 or the ContextMenu key); absent when
   *  the plan can't be edited. */
  onOpenRowMenu?: (featureId: string) => void;
}

export interface PlanShortcutOptions {
  /** The bundle picker is open. The full layer is off then, but the
   *  listener stays mounted for Cmd/Ctrl+A alone. */
  pickerOpen?: boolean;
  /** Cmd/Ctrl+A while the picker is open: tick every pickable defense. */
  onSelectAllPickable?: () => void;
}

/** Whether the plan keyboard layer is on: the panel passes this as
 *  usePlanShortcuts' `enabled`, and rows read it to decide whether to
 *  advertise a key. */
export function planShortcutsLive(state: {
  isDesktopShell: boolean;
  panelShown: boolean;
  hasOwnPlan: boolean;
  bundleSelecting: boolean;
}): boolean {
  return state.isDesktopShell && state.panelShown && state.hasOwnPlan && !state.bundleSelecting;
}

/** Where an arrow key moves the selection. Stops at both ends. With nothing
 *  (or something off screen) selected, Down takes the first row and Up the
 *  last. Null when there is nowhere to go. */
export function nextRowId(
  ids: readonly string[],
  currentId: string | null,
  dir: 1 | -1
): string | null {
  if (ids.length === 0) return null;
  const i = currentId ? ids.indexOf(currentId) : -1;
  if (i < 0) return dir === 1 ? ids[0] : ids[ids.length - 1];
  const next = Math.min(ids.length - 1, Math.max(0, i + dir));
  return ids[next];
}

/**
 * Keyboard layer for the plan editor: 1-8 pick a defense, Delete removes the
 * selected one, Cmd/Ctrl+Z runs the toast's Undo, Up/Down walk the rows, A
 * opens Assign, C opens Copy as, Shift+F10 or the ContextMenu key opens the
 * row's ⋯ menu. One capture-phase document listener, mounted only while the
 * editor is open on the desktop shell. While the bundle picker is open the
 * rest is off (`enabled` is false) and the listener handles only Cmd/Ctrl+A,
 * which ticks every pickable defense (`options`). With the layer live,
 * Cmd/Ctrl+A selects every visible row instead.
 *
 * Every key goes through shouldHandleShortcut, the guard the global shortcuts
 * use: nothing fires from a text field, over a dialog, during a presentation
 * or on a held key. The guard's "drawing" flag is fed differently for the
 * two families. Digits only stand down mid-stroke (a vertex is down), so they
 * can switch strategy while a tool is armed but idle. Everything else stands
 * down whenever a tool is armed, which leaves Backspace, Enter and Escape to
 * useDefensePlanDraw.web.ts alone (Backspace is vertex undo there; Delete
 * here never fires while drawing, so no key is handled twice).
 *
 * No collision with useGlobalShortcuts: it owns only L, K, P, N, ?, / and
 * Cmd/Ctrl+K. Digits, A, C, Delete and the arrows are free.
 */
export function usePlanShortcuts(
  actions: PlanShortcutActions,
  enabled: boolean,
  options?: PlanShortcutOptions
): void {
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const pickerOnly = !enabled && !!options?.pickerOpen;

  useEffect(() => {
    if ((!enabled && !pickerOnly) || typeof document === 'undefined') return;

    const onKeyDown = (e: KeyboardEvent) => {
      const ui = useUIStore.getState();
      const a = actionsRef.current;
      const base = { isPresenting: ui.presentationActive, isDialogOpen: isEscapeLayerOpen() };
      const toolArmed = ui.planDrawTool != null;

      // Cmd/Ctrl+A with the picker open: same hand-rolled guard as Cmd+Z, so
      // a text field (the bundle name) keeps its native select-all.
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'a') {
        const select = enabled ? a.onSelectAllRows : optionsRef.current?.onSelectAllPickable;
        if (!select) return;
        const plain = {
          key: e.key,
          metaKey: false,
          ctrlKey: false,
          altKey: false,
          shiftKey: false,
          repeat: e.repeat,
          target: e.target,
        };
        if (!shouldHandleShortcut(plain, { ...base, isDrawingPlan: ui.planStrokeActive })) return;
        e.preventDefault();
        select();
        return;
      }
      if (!enabled) return;

      // Cmd/Ctrl+Z: the guard refuses modifier combos, so check the same
      // conditions by hand: not a repeat, not typing, nothing on top.
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'z') {
        const plain = {
          key: e.key,
          metaKey: false,
          ctrlKey: false,
          altKey: false,
          shiftKey: false,
          repeat: e.repeat,
          target: e.target,
        };
        if (!shouldHandleShortcut(plain, { ...base, isDrawingPlan: ui.planStrokeActive })) return;
        if (undoLatest()) e.preventDefault();
        return;
      }

      if (/^[1-9]$/.test(e.key)) {
        if (!shouldHandleShortcut(e, { ...base, isDrawingPlan: ui.planStrokeActive })) return;
        const strategy = a.strategies[Number(e.key) - 1];
        if (!strategy) return;
        e.preventDefault();
        a.onPickStrategy(strategy);
        return;
      }

      const isRowMenu = (e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu';
      if (isRowMenu) {
        // Like the digits: only a stroke with vertices down stands this
        // down. The tool stays armed after a point commits, which is when
        // the menu gets used (Oct 15 2026 gate: Shift+F10 never opened).
        if (!shouldHandleShortcut(e, { ...base, isDrawingPlan: ui.planStrokeActive })) return;
        const selected = ui.selectedPlanFeatureId;
        if (!selected || !a.onOpenRowMenu) return;
        e.preventDefault();
        a.onOpenRowMenu(selected);
        return;
      }

      const isDelete = e.key === 'Delete' || e.key === 'Backspace';
      const isArrow = e.key === 'ArrowUp' || e.key === 'ArrowDown';
      const isAssign = e.key.toLowerCase() === 'a';
      const isCopy = e.key.toLowerCase() === 'c';
      if (!isDelete && !isArrow && !isAssign && !isCopy) return;
      if (!shouldHandleShortcut(e, { ...base, isDrawingPlan: toolArmed })) return;
      const selected = ui.selectedPlanFeatureId;
      // With 2 or more picked, A and C have no single target. Delete acts on
      // the whole set.
      const multi = ui.planMultiSelection.length >= 2;

      if (isDelete) {
        if (multi && a.onDeleteSelection) {
          e.preventDefault();
          a.onDeleteSelection([...ui.planMultiSelection]);
          return;
        }
        if (!selected) return;
        e.preventDefault();
        a.onDeleteFeature(selected);
      } else if (isAssign) {
        if (multi) return;
        if (!selected || !a.onAssignFeature) return;
        e.preventDefault();
        a.onAssignFeature(selected);
      } else if (isCopy) {
        if (multi) return;
        if (!selected || !a.onCopyFeature) return;
        e.preventDefault();
        a.onCopyFeature(selected);
      } else {
        const target = nextRowId(a.visibleFeatureIds, selected, e.key === 'ArrowDown' ? 1 : -1);
        if (!target) return;
        e.preventDefault();
        // Focus moves with the selection only when a row already has it, so
        // arrows from the map or the page body never steal focus.
        const active = document.activeElement as HTMLElement | null;
        const rowHasFocus = !!active?.id && active.id.startsWith('plan-row-btn-');
        if (target !== selected) a.onSelectFeature(target);
        if (rowHasFocus) document.getElementById(planRowButtonId(target))?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [enabled, pickerOnly]);
}
